import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../../theme';
import { Button } from '../../../../components/Button';
import { useTranslation } from '../../../../hooks/useTranslation';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const SectionTitle = ({ icon, title, colors }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    <Box sx={{ color: colors.greenAccent[500] }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{title}</Typography>
  </Box>
);

const selectSx = {
  backgroundColor: '#fff',
  fontSize: '13px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bdbdbd' },
};

const Step2Credor = ({ formData, updateFormData, onNext, onBack, options }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const s = 'createContractModal.step2';

  const isValid =
    !!formData.credor &&
    !!formData.bancarizador &&
    !!formData.ccbVia &&
    !!formData.termoCessao;

  const handleChange = (field) => (e) => updateFormData({ [field]: e.target.value });

  return (
    <Box>
      {/* Estrutura do contrato */}
      <Box sx={{ mb: 3 }}>
        <SectionTitle icon={<SettingsOutlinedIcon fontSize="small" />} title={t(`${s}.contractStructure`)} colors={colors} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.creditor`)}</InputLabel>
              <Select value={formData.credor} label={t(`${s}.creditor`)} onChange={handleChange('credor')} sx={selectSx}>
                {(options?.credores ?? []).map((c) => (
                  <MenuItem key={c.id} value={c.id} sx={{ fontSize: '13px' }}>{c.name}</MenuItem>
                ))}
              </Select>
              {!formData.credor && <FormHelperText>{t(`${s}.required`)}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.bankizer`)}</InputLabel>
              <Select value={formData.bancarizador} label={t(`${s}.bankizer`)} onChange={handleChange('bancarizador')} sx={selectSx}>
                {(options?.bancarizadores ?? []).map((b) => (
                  <MenuItem key={b.id} value={b.id} sx={{ fontSize: '13px' }}>{b.name}</MenuItem>
                ))}
              </Select>
              {!formData.bancarizador && <FormHelperText>{t(`${s}.required`)}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Minutas */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <SectionTitle icon={<DescriptionOutlinedIcon fontSize="small" />} title={t(`${s}.minutas`)} colors={colors} />
          <Typography
            sx={{
              fontSize: '13px',
              color: colors.greenAccent[500],
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => {}}
          >
            {t(`${s}.submitMinutas`)}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.ccb`)}</InputLabel>
              <Select value={formData.ccbVia} label={t(`${s}.ccb`)} onChange={handleChange('ccbVia')} sx={selectSx}>
                {(options?.minutasCcb ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: '13px' }}>{m.name}</MenuItem>
                ))}
              </Select>
              {!formData.ccbVia && <FormHelperText>{t(`${s}.required`)}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.ccbNotNeg`)}</InputLabel>
              <Select value={formData.ccbViaNaoNegociavel} label={t(`${s}.ccbNotNeg`)} onChange={handleChange('ccbViaNaoNegociavel')} sx={selectSx}>
                <MenuItem value="" sx={{ fontSize: '13px' }}><em>{t(`${s}.none`)}</em></MenuItem>
                {(options?.minutasCcbNaoNeg ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: '13px' }}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>{t(`${s}.cessaoTerm`)}</InputLabel>
              <Select value={formData.termoCessao} label={t(`${s}.cessaoTerm`)} onChange={handleChange('termoCessao')} sx={selectSx}>
                {(options?.termosCessao ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ fontSize: '13px' }}>{m.name}</MenuItem>
                ))}
              </Select>
              {!formData.termoCessao && <FormHelperText>{t(`${s}.required`)}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
        <Button variant="outlined" fullWidth={false} onClick={onBack} sx={{ minWidth: 120 }}>
          {t(`${s}.backButton`)}
        </Button>
        <Button variant="contained" fullWidth={false} disabled={!isValid} onClick={onNext} sx={{ minWidth: 120 }}>
          {t(`${s}.nextButton`)}
        </Button>
      </Box>
    </Box>
  );
};

export default Step2Credor;
